import React, { useEffect, useRef, useState } from 'react';
import {
  collection,
  query,
  getDocs,
  orderBy,
  limit as queryLimit,
  doc,
  getDoc,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChevronLeft, ChevronRight, MessageCircle, X } from 'lucide-react';

import { IMAGE_URL } from '../api/client';

interface ChatProps {
  otherUser: {
    uid: string;
    name: string;
  };
  currentUser: {
    uid: string;
    name: string;
  };
  chatId: string;
  bookingId?: string | number;
  onClose?: () => void;
}

const Chat: React.FC<ChatProps> = ({ otherUser, currentUser, chatId, bookingId, onClose }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewGallery, setPreviewGallery] = useState<{ images: string[]; index: number } | null>(
    null
  );

  const normalizeUserId = (raw: unknown): string => {
    if (raw === undefined || raw === null) return '';
    return String(raw)
      .trim()
      .replace(/^["']+|["']+$/g, '')
      .replace(/^\/?users\//i, '')
      .trim();
  };

  const normalizeBookingId = (raw: unknown): string => {
    if (raw === undefined || raw === null) return '';
    return String(raw).trim();
  };

  const extractUserId = (ref: any): string => {
    if (!ref) return '';

    if (typeof ref === 'string') {
      const parts = ref.split('/').filter(Boolean);
      return parts[parts.length - 1] || '';
    }

    if (typeof ref === 'object') {
      if (typeof ref.id === 'string') return ref.id;
      if (typeof ref.path === 'string') {
        const parts = ref.path.split('/').filter(Boolean);
        return parts[parts.length - 1] || '';
      }
      const segments = ref?._key?.path?.segments;
      if (Array.isArray(segments) && segments.length > 0) return segments[segments.length - 1];
    }

    return '';
  };

  const getSenderId = (msg: any): string => {
    const userIdFromUserRef = normalizeUserId(extractUserId(msg?.userRef));
    const userIdFromToUserRef = normalizeUserId(extractUserId(msg?.toUserRef));
    const normalizedCurrentUserId = normalizeUserId(currentUser.uid);
    const normalizedOtherUserId = normalizeUserId(otherUser.uid);

    if (
      userIdFromUserRef === normalizedCurrentUserId ||
      userIdFromUserRef === normalizedOtherUserId
    ) {
      return userIdFromUserRef;
    }

    if (userIdFromToUserRef === normalizedCurrentUserId) return normalizedOtherUserId;
    if (userIdFromToUserRef === normalizedOtherUserId) return normalizedCurrentUserId;

    return userIdFromUserRef || userIdFromToUserRef || '';
  };

  const getFileType = (url: string | any) => {
    if (!url || typeof url !== 'string') return 'file';
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/)) return 'image';
    if (lowerUrl.match(/\.(mp4|webm|ogg|mov|avi)$/)) return 'video';
    if (lowerUrl.match(/\.(pdf|doc|docx|xls|xlsx|txt|zip|rar)$/)) return 'file';
    return 'file';
  };

  const getFileName = (url: string | any) => {
    if (!url || typeof url !== 'string') return 'file';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.split('/').pop() || 'file';
    } catch {
      return url.split('/').pop() || 'file';
    }
  };

  const getFileUrl = (file: any): string | null => {
    if (!file) return null;
    if (typeof file === 'string') return file;
    if (file.file && typeof file.file === 'string') {
      const filename = file.file;
      const s3BaseUrl = `${IMAGE_URL}/chat/`;
      return `${s3BaseUrl}${filename}`;
    }

    if (file.url && typeof file.url === 'string') return file.url;
    if (file.path && typeof file.path === 'string') return file.path;
    if (file.downloadURL && typeof file.downloadURL === 'string') return file.downloadURL;
    if (file.href && typeof file.href === 'string') return file.href;
    if (file.src && typeof file.src === 'string') return file.src;

    console.warn('Could not extract URL from file object:', file);
    return null;
  };

  const mapConversationMessages = (docs: any[]) =>
    docs
      .map((d) => {
        const msgData = d.data();
        return {
          id: d.id,
          message: msgData.message || '',
          userRef: msgData.userRef,
          toUserRef: msgData.toUserRef,
          createdAt: msgData.createdAt,
          bookingId: msgData.bookingId ?? msgData.bookingID ?? msgData.booking_id,
          type: msgData.type || 'text',
          files: msgData.files,
          isDeleted: msgData.isDeleted,
        };
      })
      .filter((message) => {
        if (!bookingId) return true;
        if (message.bookingId === undefined || message.bookingId === null) return true;
        return normalizeBookingId(message.bookingId) === normalizeBookingId(bookingId);
      })
      .sort((a, b) => {
        const aTime =
          a.createdAt && typeof a.createdAt.toDate === 'function'
            ? a.createdAt.toDate().getTime()
            : new Date(a.createdAt || 0).getTime();
        const bTime =
          b.createdAt && typeof b.createdAt.toDate === 'function'
            ? b.createdAt.toDate().getTime()
            : new Date(b.createdAt || 0).getTime();
        return aTime - bTime;
      });

  // Fetch live chat messages from Firebase
  useEffect(() => {
    let isMounted = true;
    let unsubscribeConversations: (() => void) | null = null;

    const findConnectionRef = async (currentUid: string, otherUid: string) => {
      const hasMatchingBookingId = (data: Record<string, unknown>) => {
        if (!bookingId) return true;
        const rawBookingId = data.bookingId ?? data.bookingID ?? data.booking_id;
        if (rawBookingId === undefined || rawBookingId === null || String(rawBookingId).trim() === '') {
          return true;
        }
        return normalizeBookingId(rawBookingId) === normalizeBookingId(bookingId);
      };

      const connectionIdsToTry = Array.from(
        new Set(
          [
            chatId,
            `${currentUid}-${otherUid}`,
            `${otherUid}-${currentUid}`,
            `${currentUid}_${otherUid}`,
            `${otherUid}_${currentUid}`,
          ].filter(Boolean)
        )
      );

      for (const connectionId of connectionIdsToTry) {
        const connectionRef = doc(db, 'connections', connectionId);
        const connectionDoc = await getDoc(connectionRef);
        if (!connectionDoc.exists()) continue;
        const connectionData = connectionDoc.data() as Record<string, unknown>;
        if (hasMatchingBookingId(connectionData)) return connectionRef;
      }

      const currentMemberVariants = Array.from(
        new Set([currentUid, `/users/${currentUid}`, `"${currentUid}"`])
      );

      for (const currentMemberValue of currentMemberVariants) {
        const connectionsQuery = query(
          collection(db, 'connections'),
          where('members', 'array-contains', currentMemberValue),
          queryLimit(40)
        );
        const connectionsSnapshot = await getDocs(connectionsQuery);
        const matchedConnection = connectionsSnapshot.docs.find((d) => {
          const data = d.data() as Record<string, unknown>;
          if (!hasMatchingBookingId(data)) return false;

          const members = Array.isArray(data.members) ? data.members : [];
          const normalizedMembers = members.map((m: unknown) => normalizeUserId(m));
          return normalizedMembers.includes(currentUid) && normalizedMembers.includes(otherUid);
        });

        if (matchedConnection) return matchedConnection.ref;
      }

      return null;
    };

    const startLiveMessages = async () => {
      try {
        const currentUid = normalizeUserId(currentUser.uid);
        const otherUid = normalizeUserId(otherUser.uid);
        if (!currentUid || !otherUid) {
          if (isMounted) {
            setMessages([]);
            setIsLoading(false);
          }
          return;
        }

        const connectionRef = await findConnectionRef(currentUid, otherUid);
        if (!connectionRef) {
          if (isMounted) {
            setMessages([]);
            setIsLoading(false);
          }
          return;
        }

        const conversationsRef = collection(connectionRef, 'conversations');
        let fallbackStarted = false;

        const startFallbackListener = () => {
          if (fallbackStarted) return;
          fallbackStarted = true;
          unsubscribeConversations = onSnapshot(
            query(conversationsRef, queryLimit(200)),
            (snapshot) => {
              if (!isMounted) return;
              setMessages(mapConversationMessages(snapshot.docs));
              setIsLoading(false);
            },
            () => {
              if (!isMounted) return;
              setMessages([]);
              setIsLoading(false);
            }
          );
        };

        try {
          unsubscribeConversations = onSnapshot(
            query(conversationsRef, orderBy('createdAt', 'asc'), queryLimit(200)),
            (snapshot) => {
              if (!isMounted) return;
              setMessages(mapConversationMessages(snapshot.docs));
              setIsLoading(false);
            },
            () => {
              startFallbackListener();
            }
          );
        } catch {
          startFallbackListener();
        }
      } catch (err: any) {
        if (isMounted) {
          setMessages([]);
          setIsLoading(false);
        }
      }
    };

    startLiveMessages();

    return () => {
      isMounted = false;
      if (unsubscribeConversations) unsubscribeConversations();
    };
  }, [otherUser.uid, currentUser.uid, chatId, bookingId]);

  // Read-only admin view - no sending functionality

  const renderDateHeader = (date: Date) => {
    const now = new Date();
    const msgDate = new Date(date);

    const isToday =
      msgDate.getDate() === now.getDate() &&
      msgDate.getMonth() === now.getMonth() &&
      msgDate.getFullYear() === now.getFullYear();

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const isYesterday =
      msgDate.getDate() === yesterday.getDate() &&
      msgDate.getMonth() === yesterday.getMonth() &&
      msgDate.getFullYear() === yesterday.getFullYear();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return msgDate.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const groupMessagesByDate = (messages: any[]) => {
    const groups: { [date: string]: any[] } = {};

    messages.forEach((message) => {
      // Handle Firebase timestamp properly
      let date: Date;
      if (message.createdAt && typeof message.createdAt.toDate === 'function') {
        // Firebase Timestamp
        date = message.createdAt.toDate();
      } else if (message.createdAt instanceof Date) {
        // JavaScript Date
        date = message.createdAt;
      } else if (typeof message.createdAt === 'string' || typeof message.createdAt === 'number') {
        // String or number timestamp
        date = new Date(message.createdAt);
      } else {
        // Fallback
        console.warn('⚠️ Invalid createdAt format:', message.createdAt);
        date = new Date();
      }

      const dateKey = date.toISOString().slice(0, 10);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push({ ...message, createdAt: date });
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-[98%] max-w-6xl h-[88vh] max-h-[920px] flex flex-col">
        {/* Header */}
        <div className="bg-indigo-600 text-white p-3 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center">
            <MessageCircle className="w-6 h-6 mr-2" />
            <div>
              <div className="font-medium text-base">
                {otherUser.name} - {currentUser.name}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto p-3 bg-gray-50 flex flex-col-reverse">
          <div ref={listRef} />

          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="loader">
                  <div className="box-1">{/* <span>LOADING</span> */}</div>
                </div>
              </div>
            </div>
          )}

          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center mt-[-500px]">
              <img
                src="/noconvo.jpg"
                alt="No messages"
                className="w-96 h-100 object-contain  pb-[100px]"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}

          {Object.entries(messageGroups)
            .reverse()
            .map(([dateKey, dateMessages]) => (
              <div key={dateKey} className="mb-3">
                {/* Date Header */}
                <div className="text-center my-3">
                  <span className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-md text-sm font-semibold border border-indigo-200">
                    {renderDateHeader(new Date(dateKey))}
                  </span>
                </div>

                {/* Messages for this date */}
                {dateMessages.map((item, index) => {
                  const senderId = getSenderId(item);
                  const isAjay = senderId === normalizeUserId(otherUser.uid);
                  const isRight = isAjay;
                  const senderName = isAjay ? otherUser.name : currentUser.name;

                  // Handle Firebase timestamp properly for time display
                  let date: Date;
                  if (item.createdAt && typeof item.createdAt.toDate === 'function') {
                    date = item.createdAt.toDate();
                  } else if (item.createdAt instanceof Date) {
                    date = item.createdAt;
                  } else if (
                    typeof item.createdAt === 'string' ||
                    typeof item.createdAt === 'number'
                  ) {
                    date = new Date(item.createdAt);
                  } else {
                    date = new Date();
                  }
                  const time = date.toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  });

                  // Check if message has media files
                  const hasFiles = item.files && Array.isArray(item.files) && item.files.length > 0;
                  const messageImageUrls = hasFiles
                    ? item.files
                        .map((file: any) => getFileUrl(file))
                        .filter((url: string | null) => !!url && getFileType(url) === 'image')
                    : [];

                  return (
                    <div
                      key={item.id}
                      className={`flex ${isRight ? 'justify-start' : 'justify-end'} mb-2`}
                    >
                      <div className={`max-w-[80%]`}>
                        <div
                          className={`px-3 py-2 text-sm border ${isRight
                            ? 'bg-gray-50 text-gray-900 rounded-2xl rounded-tl-sm border-gray-200'
                            : 'bg-blue-50 text-gray-900 rounded-2xl rounded-tr-sm border-blue-200'
                            }`}
                        >
                          <div className={`text-sm mb-1 ${isRight ? 'text-black' : 'text-black'}`}>
                            <span className="font-bold">{senderName}</span> <span>{time}</span>
                          </div>

                          {/* Text message */}
                          {item.message && (
                            <div className="whitespace-pre-wrap break-words mb-2">{item.message}</div>
                          )}

                          {/* Media files */}
                          {hasFiles && (
                            <div className="space-y-2 mt-2">
                              {item.files.map((file: any, fileIndex: number) => {
                                const fileUrl = getFileUrl(file);

                                // Skip if couldn't extract valid URL
                                if (!fileUrl) return null;

                                const fileType = getFileType(fileUrl);

                                if (fileType === 'image') {
                                  const imageIndex = messageImageUrls.findIndex((url: string) => url === fileUrl);
                                  return (
                                    <div key={fileIndex} className="rounded-lg overflow-hidden">
                                      <img
                                        src={fileUrl}
                                        alt="Shared image"
                                        className="max-w-full h-auto max-h-48 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() =>
                                          setPreviewGallery({
                                            images: messageImageUrls,
                                            index: imageIndex >= 0 ? imageIndex : 0,
                                          })
                                        }
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.src = '/placeholder-image.png';
                                        }}
                                      />
                                    </div>
                                  );
                                }

                                if (fileType === 'video') {
                                  return (
                                    <div key={fileIndex} className="rounded-lg overflow-hidden">
                                      <video
                                        controls
                                        className="max-w-full h-auto max-h-80 rounded-lg"
                                        preload="metadata"
                                      >
                                        <source src={fileUrl} type="video/mp4" />
                                        Your browser does not support the video tag.
                                      </video>
                                    </div>
                                  );
                                }

                                // File download link
                                return (
                                  <a
                                    key={fileIndex}
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 p-2 rounded border ${isRight
                                      ? 'bg-white border-gray-300 hover:bg-gray-100'
                                      : 'bg-white border-blue-300 hover:bg-blue-50'
                                      } transition-colors`}
                                  >
                                    <svg
                                      className="w-5 h-5 text-gray-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                    <span className="text-sm text-gray-700 truncate max-w-xs">
                                      {getFileName(fileUrl)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      ({fileType})
                                    </span>
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          <div ref={listRef} />
        </div>

        {/* Admin Read-Only Notice */}
        <div className="bg-gray-50 p-2 rounded-b-lg border-t border-gray-200">
          <div className="flex-1 text-center">
            <b className="text-xs">🔒 Admin Access — Read-Only Permissions Enabled</b>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewGallery && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60]"
          onClick={() => setPreviewGallery(null)}
        >
          <div
            className="relative w-full max-w-5xl p-6 flex flex-col items-center gap-6"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={previewGallery.images[previewGallery.index]}
              alt="Chat media preview"
              className="w-full max-h-[75vh] object-contain rounded-xl bg-white shadow-2xl"
            />

            {previewGallery.images.length > 1 && (
              <div className="flex items-center gap-4 bg-white/95 backdrop-blur-md rounded-xl px-6 py-3 shadow-lg">
                <button
                  type="button"
                  className="p-3 rounded-full border border-blue-200 bg-blue-100 shadow-md hover:bg-blue-200 hover:scale-105 active:scale-95 transition-all duration-200"
                  onClick={() =>
                    setPreviewGallery((prev) =>
                      prev
                        ? {
                            ...prev,
                            index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1,
                          }
                        : prev
                    )
                  }
                >
                  <ChevronLeft className="w-6 h-6 text-blue-600" />
                </button>

                <span className="text-sm font-semibold text-gray-700">
                  {previewGallery.index + 1} / {previewGallery.images.length}
                </span>

                <button
                  type="button"
                  className="p-3 rounded-full border border-blue-200 bg-blue-100 shadow-md hover:bg-blue-200 hover:scale-105 active:scale-95 transition-all duration-200"
                  onClick={() =>
                    setPreviewGallery((prev) =>
                      prev
                        ? {
                            ...prev,
                            index: prev.index === prev.images.length - 1 ? 0 : prev.index + 1,
                          }
                        : prev
                    )
                  }
                >
                  <ChevronRight className="w-6 h-6 text-blue-600" />
                </button>
              </div>
            )}

            <button
              className="absolute top-10 right-10 bg-blue-100 border border-blue-200 rounded-full p-3 shadow-md hover:bg-blue-200 transition-all duration-300"
              onClick={() => setPreviewGallery(null)}
            >
              <X className="w-7 h-7 text-blue-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
